import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import {
  fetchAsyncEditFormOptions,
  fetchAsyncGetMinorSubCategory,
  fetchAsyncEditSubCategory,
  fetchAsyncDeleteMinorSubCategory,
  fetchAsyncEditMinorSubCategory,
  fetchAsyncCreateMinorSubCategory,
  fetchAsyncDeleteSubCategory,
  fetchAsyncCreateSubCategory,
  fetchAsyncGetSubCategory,
  fetchAsyncDeleteFormOptions,
  fetchAsyncAddValueToFormOptions,
} from "redux/formOptions";

const showSuccessAlert = (message) => {
  Swal.fire({
    title: message || "Successfully Added",
    icon: "success",
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Ok",
    customClass: {
      container: "sweet_containerImportant",
      confirmButton: "sweet_confirmbuttonImportant",
    },
  });
};

const showErrorAlert = (message) => {
  Swal.fire({
    title: message || "Something went wrong",
    icon: "warning",
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Yes",
    customClass: {
      container: "sweet_containerImportant",
      confirmButton: "sweet_confirmbuttonImportant",
    },
  });
};

const useFormOptionsApi = () => {
  const dispatch = useDispatch();

  const createFormOptionApi = async (data, type, expenseType) => {
    try {
      const res = await dispatch(
        fetchAsyncAddValueToFormOptions({
          label: data.label,
          value: data.value,
          type,
          expenseType,
        })
      );

      if (res.payload.val) {
        showSuccessAlert();
        return {
          label: res.payload.lab,
          value: res.payload.val,
          id: res.payload?._id,
        };
      }
    } catch (err) {
      showErrorAlert();
      return null;
    }
  };

  const deleteFormOptionApi = async (_id) => {
    if (_id) {
      const response = await dispatch(fetchAsyncGetSubCategory(_id));
      if (response.payload) {
        const minorOption = response.payload || [];
        if (minorOption.length > 0) {
          return { success: false, message: 'Please delete all related Minor Type first' };
        } else {
          const deleteResponse = await dispatch(fetchAsyncDeleteFormOptions(_id));
          if (deleteResponse.payload) {
            return { success: true, message: 'Option deleted successfully!' };
          }
        }
      }
    }
    return { success: false, message: 'No ID provided' };
  };

  const editFormOptionApi = async (_id, newValue, expenseType) => {
    if (_id) {
      try {
        const res = await dispatch(fetchAsyncEditFormOptions({
          id: _id,
          newData: {
            optionValue: newValue,
            expenseType,
          },
        }));

        if (res.payload.val) {
          return { success: true, data: { label: res.payload.lab, value: res.payload.val, id: res.payload._id } };
        }
      } catch (err) {
        return { success: false, message: 'Error editing option' };
      }
    }
    return { success: false, message: 'No ID provided' };
  };

  // Subcategory APIs
  const createSubCategoryApi = async (optionId, category, expenseType) => {
    try {
      const res = await dispatch(fetchAsyncCreateSubCategory({ id: optionId, category, expenseType }));
      if (res.payload && res.payload.val) {
        return {
          success: true,
          data: {
            id: res.payload._id,
            label: res.payload.lab,
            value: res.payload.val,
            expenseType: res.payload?.expTyp,
          },
        };
      }
    } catch (error) {
      return { success: false, message: 'Error creating subcategory' };
    }
    return { success: false, message: 'Failed to create subcategory' };
  };

  const deleteSubCategoryApi = async (id) => {
    try {
      const response = await dispatch(fetchAsyncGetMinorSubCategory(id));
      if (response.payload) {
        const minorSubOption = response.payload || [];
        if (minorSubOption.length > 0) {
          return { success: false, message: 'Please delete all related Minor Sub Category first' };
        } else {
          const deleteResponse = await dispatch(fetchAsyncDeleteSubCategory(id));
          if (deleteResponse.payload && deleteResponse.payload.val) {
            return { success: true, value: deleteResponse.payload.val };
          }
        }
      }
    } catch (error) {
      return { success: false, message: 'Error deleting subcategory' };
    }
    return { success: false, message: 'Failed to delete subcategory' };
  };

  const editSubCategoryApi = async (_id, newValue, expenseType) => {
    try {
      const res = await dispatch(fetchAsyncEditSubCategory({
        id: _id,
        newData: {
          optionValue: newValue,
          expenseType,
        },
      }));

      if (res.payload && res.payload.val) {
        return { success: true, id: _id };
      }
    } catch (error) {
      return { success: false, message: 'Error editing subcategory' };
    }
    return { success: false, message: 'Failed to edit subcategory' };
  };

  const getSubCategoryApi = async (expenseId) => {
    try {
      const response = await dispatch(fetchAsyncGetSubCategory(expenseId));
      if (response.payload) {
        return { success: true, data: response.payload || [] };
      }
    } catch (error) {
      return { success: false, message: 'Error fetching subcategories' };
    }
    return { success: false, message: 'Failed to fetch subcategories' };
  };

  const createMinorSubCategoryApi = async (optionId, category, expenseType) => {
    try {
      const res = await dispatch(fetchAsyncCreateMinorSubCategory({ id: optionId, category, expenseType }));
      if (res.payload && res.payload.val) {
        return {
          success: true,
          data: {
            id: res.payload._id,
            label: res.payload.lab,
            value: res.payload.val,
            expenseType: res.payload?.expTyp,
          },
        };
      }
    } catch (error) {
      return { success: false, message: 'Error creating minor subcategory' };
    }
    return { success: false, message: 'Failed to create minor subcategory' };
  };

  const deleteMinorSubCategoryApi = async (id) => {
    try {
      const res = await dispatch(fetchAsyncDeleteMinorSubCategory(id));
      if (res.payload && res.payload.val) {
        return { success: true, value: res.payload.val };
      }
    } catch (error) {
      return { success: false, message: 'Error deleting minor subcategory' };
    }
    return { success: false, message: 'Failed to delete minor subcategory' };
  };

  const editMinorSubCategoryApi = async (_id, newValue, expenseType) => {
    try {
      const res = await dispatch(fetchAsyncEditMinorSubCategory({
        id: _id,
        newData: {
          optionValue: newValue,
          expenseType,
        },
      }));

      if (res.payload && res.payload.val) {
        return { success: true, id: _id };
      }
    } catch (error) {
      return { success: false, message: 'Error editing minor subcategory' };
    }
    return { success: false, message: 'Failed to edit minor subcategory' };
  };

  const getMinorSubCategoryApi = async (expenseId) => {
    try {
      const response = await dispatch(fetchAsyncGetMinorSubCategory(expenseId));
      if (response.payload) {
        return { success: true, data: response.payload || [] };
      }
    } catch (error) {
      return { success: false, message: 'Error fetching minor subcategories' };
    }
    return { success: false, message: 'Failed to fetch minor subcategories' };
  };

  return {
    createFormOptionApi,
    deleteFormOptionApi,
    editFormOptionApi,
    createSubCategoryApi,
    deleteSubCategoryApi,
    editSubCategoryApi,
    getSubCategoryApi,
    createMinorSubCategoryApi,
    deleteMinorSubCategoryApi,
    editMinorSubCategoryApi,
    getMinorSubCategoryApi,
  };
};

export default useFormOptionsApi;
